/**
 * User-settings helpers for spec 010 US3 — wraps GET/PATCH /me/settings.
 *
 * The PATCH endpoint takes a partial object with `additionalProperties: false`
 * and (per docstring) `minProperties=1`. `patchMySettings` validates the
 * partial client-side so the server never sees an empty body, and returns a
 * structured 422 (`VoiceValidationError`) the caller can hand directly to
 * `voiceCatalogue.refreshFromErrorBody`.
 */

import axios from 'axios';
import { getApiUrl } from '../config';
import { getAccessToken } from './auth';

// Per spec 010 FR-006 / FR-008, the UserSettings PATCH surface is limited
// to the three fields the Settings UI exposes. The PhaseTriggerRequest
// `tts_model` override is API-only and is NOT a UserSettings field.
const PATCH_ALLOWED = new Set(['automatic_processing', 'automatic_blitz', 'default_voice']);

export class EmptyPatchError extends Error {
  constructor() {
    super('patchMySettings: partial must contain at least one field.');
    this.name = 'EmptyPatchError';
  }
}

export class VoiceValidationError extends Error {
  constructor(body) {
    super((body && body.error) || 'Voice is not supported by the active TTS model.');
    this.name = 'VoiceValidationError';
    this.body = body || null;
    this.rejected_value = body && body.rejected_value ? body.rejected_value : null;
    this.voice_catalogue = body && Array.isArray(body.voice_catalogue) ? body.voice_catalogue : [];
  }
}

function authHeaders() {
  const token = getAccessToken();
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

export async function getMySettings() {
  const response = await axios.get(getApiUrl('me/settings'), { headers: authHeaders() });
  return response && response.data ? response.data : null;
}

/**
 * PATCH /me/settings with a partial body.
 * - Only keys in `PATCH_ALLOWED` are forwarded.
 * - Refuses an empty patch with `EmptyPatchError` (saves a guaranteed 422).
 * - Translates 422 into `VoiceValidationError` so the caller can refresh the
 *   voice catalogue and surface the `error` text inline.
 */
function filterPatchBody(partial) {
  if (!partial || typeof partial !== 'object') return null;
  const body = {};
  for (const key of Object.keys(partial)) {
    if (PATCH_ALLOWED.has(key)) body[key] = partial[key];
  }
  return Object.keys(body).length > 0 ? body : null;
}

function maybeRethrowAsVoiceError(err) {
  const status = err && err.response ? err.response.status : null;
  const data = err && err.response && err.response.data ? err.response.data : null;
  if (status === 422 && data) throw new VoiceValidationError(data);
  throw err;
}

export async function patchMySettings(partial) {
  const body = filterPatchBody(partial);
  if (!body) throw new EmptyPatchError();
  try {
    const response = await axios.patch(getApiUrl('me/settings'), body, { headers: authHeaders() });
    return response && response.data ? response.data : null;
  } catch (err) {
    return maybeRethrowAsVoiceError(err);
  }
}
